import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight } from '@phosphor-icons/react';

const PaymentSuccess = () => {
  const { t, language, API, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('datatransTrxId') || searchParams.get('wtx') || searchParams.get('session_id');
  const isDatatrans = !!searchParams.get('datatransTrxId');
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    // Extended: 12 attempts × 2s + backoff = ~60s. Datatrans (esp. Apple Pay)
    // can take 30-60s to settle after the wallet closes, and Datatrans's
    // webhook can lag further. We keep polling until either the backend
    // reconciles the txn (via the active-poll sweeper on our side) or we
    // time out — at which point the user still gets a friendly message.
    const maxAttempts = 12;
    // Linear 2s for first 6 attempts (12s), then 4s (32s), 6s (56s) …
    const intervalFor = (i) => (i < 6 ? 2000 : i < 9 ? 4000 : 6000);

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const url = isDatatrans
        ? `/payments/datatrans/status/${sessionId}`
        : `/payments/status/${sessionId}`;
      const { data } = await API.get(url);
      setPaymentInfo(data);
      
      if (data.status === 'paid') {
        setStatus('success');
        await checkAuth(); // Refresh user data to update credits
        return;
      } else if (data.status === 'expired' || data.status === 'failed') {
        setStatus('expired');
        return;
      }

      // Continue polling with backoff
      setTimeout(() => pollPaymentStatus(attempts + 1), intervalFor(attempts));
    } catch (error) {
      console.error('Error checking payment status:', error);
      // A single transient error shouldn't kill the poll — try again if we
      // still have attempts left.
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), intervalFor(attempts));
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="payment-success">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md"
      >
        {status === 'checking' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
              <svg className="animate-spin h-10 w-10 text-blue-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {language === 'de' ? 'Zahlung wird überprüft...' : 'Checking payment...'}
            </h1>
            <p className="text-slate-500">
              {language === 'de' ? 'Bitte warten Sie einen Moment' : 'Please wait a moment'}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-50 flex items-center justify-center">
              <CheckCircle size={48} weight="fill" className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {language === 'de' ? 'Zahlung erfolgreich!' : 'Payment Successful!'}
            </h1>
            <p className="text-slate-500 mb-2">
              {language === 'de' ? 'Ihre Buchung wurde bestätigt.' : 'Your booking has been confirmed.'}
            </p>
            {paymentInfo && (
              <p className="text-emerald-600 font-bold text-xl mb-6">
                {(paymentInfo.amount || 0).toFixed(2)} CHF
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard" className="btn-primary flex items-center justify-center gap-2">
                {t('dashboard')}
                <ArrowRight size={18} />
              </Link>
              <Link to="/book" className="btn-secondary">
                {t('bookCourt')}
              </Link>
            </div>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-50 flex items-center justify-center">
              <span className="text-4xl">⏱️</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {language === 'de' ? 'Zeitüberschreitung' : 'Timeout'}
            </h1>
            <p className="text-slate-500 mb-6">
              {language === 'de' 
                ? 'Die Zahlungsbestätigung dauert länger als erwartet. Bitte überprüfen Sie Ihre E-Mail.'
                : 'Payment confirmation is taking longer than expected. Please check your email.'}
            </p>
            <Link to="/dashboard" className="btn-primary">
              {t('dashboard')}
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
              <span className="text-4xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {language === 'de' ? 'Fehler' : 'Error'}
            </h1>
            <p className="text-slate-500 mb-6">
              {language === 'de' 
                ? 'Beim Überprüfen der Zahlung ist ein Fehler aufgetreten.'
                : 'An error occurred while checking the payment.'}
            </p>
            <Link to="/dashboard" className="btn-primary">
              {t('dashboard')}
            </Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
              <span className="text-4xl">⏰</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {language === 'de' ? 'Sitzung abgelaufen' : 'Session Expired'}
            </h1>
            <p className="text-slate-500 mb-6">
              {language === 'de' 
                ? 'Die Zahlungssitzung ist abgelaufen. Bitte versuchen Sie es erneut.'
                : 'The payment session has expired. Please try again.'}
            </p>
            <Link to="/book" className="btn-primary">
              {language === 'de' ? 'Erneut buchen' : 'Book Again'}
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
